/**
 * AcceptInvitePage - Public page for accepting team invitations via Entra auth
 * URL: /invite/:token
 */

import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { AuthBrandImage } from '../../components/auth/AuthBrandImage';
import { EntraProviderButtons } from '../../components/auth/ProviderButtons';
import { slideUp } from '../../styles/animations';
import invitationService from '../../services/api/invitationService';

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.backgrounds.page};
  padding: ${({ theme }) => theme.spacing[4]};
`;

const FormCard = styled.div`
  width: 100%;
  max-width: 440px;
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: ${({ theme }) => theme.spacing[8]};
  animation: ${slideUp} 0.5s ease-out;
`;

const LogoArea = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 ${({ theme }) => theme.spacing[6]} 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
`;

const OrgName = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.status.error.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.error.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.status.error.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const ErrorActions = styled.div`
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

export const AcceptInvitePage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const { data: invite, error: inviteQueryError } = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => invitationService.getInvitationByToken(token!),
    enabled: !!token,
    retry: false,
  });

  const inviteErrorStatus = (inviteQueryError as any)?.response?.status ?? 0;
  const inviteError =
    inviteQueryError != null && (inviteErrorStatus === 400 || inviteErrorStatus === 404);

  if (!token) {
    return (
      <Container>
        <FormCard>
          <ErrorBanner>
            <AlertCircle />
            Invalid invitation link. Please check your email and try again.
          </ErrorBanner>
        </FormCard>
      </Container>
    );
  }

  if (inviteError) {
    return (
      <Container>
        <FormCard>
          <ErrorBanner>
            <AlertCircle />
            This invitation link is invalid, expired, or has already been used.
          </ErrorBanner>
          <ErrorActions>
            <Button variant="ghost" onClick={() => navigate('/login')} fullWidth>
              Back to sign in
            </Button>
          </ErrorActions>
        </FormCard>
      </Container>
    );
  }

  const orgName = invite?.organizationName;

  return (
    <Container>
      <FormCard>
        <LogoArea>
          <AuthBrandImage />
        </LogoArea>

        <Title>You've been invited</Title>
        <Subtitle>
          {orgName ? (
            <>
              You've been invited to join <OrgName>{orgName}</OrgName>. Choose your sign-in
              method to accept the invitation.
            </>
          ) : (
            'Choose your sign-in method to accept the invitation.'
          )}
        </Subtitle>

        <EntraProviderButtons invitationToken={token} />
      </FormCard>
    </Container>
  );
};

export default AcceptInvitePage;
